// Port of GroundTeam/Support/PasswordPolicy.swift (WEB_PLAN.md §4.2). The team server's password rules
// (Supabase Auth `password_requirements = "lower_upper_letters_digits_symbols"`, `minimum_password_length = 12`),
// checked in the browser so people see what's missing before GoTrue answers `weak_password`.
//
// Lengths are counted in UTF-8 BYTES, not characters, exactly like the iOS app: GoTrue measures bytes, and bcrypt
// reads only the first 72 bytes, so the server refuses anything longer. Letters, digits and symbols count only in
// their ASCII form, as on the server (an "é" is neither a lowercase letter nor a symbol there).

export const PASSWORD_MIN_BYTES = 12;
/** bcrypt reads only the first 72 bytes, so the server refuses longer passwords. */
export const PASSWORD_MAX_BYTES = 72;

export type PasswordRule = 'length' | 'maxLength' | 'lowercase' | 'uppercase' | 'digit' | 'symbol';

/** Display order (Swift `Rule.allCases`). */
export const PASSWORD_RULES: readonly PasswordRule[] = ['length', 'maxLength', 'lowercase', 'uppercase', 'digit', 'symbol'];

/** Checklist labels, word for word from `PasswordPolicy.Rule.title`. */
export const PASSWORD_RULE_TITLES: Readonly<Record<PasswordRule, string>> = {
  length: '12+ characters',
  maxLength: '72 characters or fewer',
  lowercase: 'A lowercase letter',
  uppercase: 'An uppercase letter',
  digit: 'A number',
  symbol: 'A symbol',
};

/** The confirmation row the iOS checklist adds when a confirm field is shown. */
export const PASSWORDS_MATCH_TITLE = 'Passwords match';

const encoder = new TextEncoder();

/** UTF-8 byte length (what GoTrue and bcrypt count). */
export function utf8ByteLength(text: string): number {
  return encoder.encode(text).length;
}

// Character classes on Unicode scalar values, as PasswordPolicy.swift does.
const isLowercase = (v: number) => v >= 0x61 && v <= 0x7a; // a-z
const isUppercase = (v: number) => v >= 0x41 && v <= 0x5a; // A-Z
const isDigit = (v: number) => v >= 0x30 && v <= 0x39; // 0-9
/** Printable ASCII punctuation (!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~), the server's symbol set. Not space. */
const isSymbol = (v: number) => v >= 0x21 && v <= 0x7e && !isLowercase(v) && !isUppercase(v) && !isDigit(v);

function scalars(text: string): number[] {
  const out: number[] = [];
  // for…of walks code points, so a surrogate pair is one scalar like in Swift's unicodeScalars.
  for (const ch of text) out.push(ch.codePointAt(0) ?? 0);
  return out;
}

/** The rules `password` doesn't meet yet, in display order (empty = valid). */
export function unmetPasswordRules(password: string): PasswordRule[] {
  const bytes = utf8ByteLength(password);
  const values = scalars(password);
  return PASSWORD_RULES.filter((rule) => {
    switch (rule) {
      case 'length':
        return bytes < PASSWORD_MIN_BYTES;
      case 'maxLength':
        return bytes > PASSWORD_MAX_BYTES;
      case 'lowercase':
        return !values.some(isLowercase);
      case 'uppercase':
        return !values.some(isUppercase);
      case 'digit':
        return !values.some(isDigit);
      case 'symbol':
        return !values.some(isSymbol);
    }
  });
}

export function isValidPassword(password: string): boolean {
  return unmetPasswordRules(password).length === 0;
}

export interface PasswordChecklistItem {
  /** The rule, or 'match' for the confirmation row. */
  id: PasswordRule | 'match';
  label: string;
  met: boolean;
}

/**
 * The live checklist under a new-password field (Swift `PasswordRulesChecklist`): every rule except the maximum
 * length, which almost no one reaches and is listed only while it's exceeded; with `confirmation`, also
 * "Passwords match".
 */
export function passwordChecklist(password: string, confirmation?: string): PasswordChecklistItem[] {
  const unmet = new Set(unmetPasswordRules(password));
  const items: PasswordChecklistItem[] = PASSWORD_RULES
    .filter((rule) => rule !== 'maxLength' || unmet.has(rule))
    .map((rule) => ({ id: rule, label: PASSWORD_RULE_TITLES[rule], met: !unmet.has(rule) }));
  if (confirmation !== undefined) {
    items.push({ id: 'match', label: PASSWORDS_MATCH_TITLE, met: confirmation !== '' && confirmation === password });
  }
  return items;
}
